/**
 * Per-fixture-type clearance and collision configuration.
 * Used by checkPlacement to determine which checks apply and what margins to use.
 *
 * Clearance values sourced from:
 * - bathroom-analyzer/pipeline/types.js (FIXTURE_DIMENSIONS)
 * - functions/rules/toiletRules.js (TOILET_SIDE_CLEARANCE)
 * - functions/rules/vanityRules.js (MIN_FRONT_CLEARANCE)
 */

const {
  SINK_TYPE,
  KITCHEN_SINK_TYPE,
  COUNTERTOP_MOUNTED_SINK_TYPES,
} = require('../sink-types');
// The predicate only — `../staging` is the whole planner, which the
// collision core has no business loading for a one-line test.
const { isStagingDecor } = require('../staging/ownership');

const INCHES = 0.0254;

// Per-sink basin clearance width is fixed at the default starting sink
// width (independent of vanity/cabinet width). Used both by the pipeline
// (sink fixtures get this clearance width regardless of how wide they grow
// during stage-6 expansion) and by the standalone collision system's
// per-sink footprint helper.
const BASIN_CLEARANCE_WIDTH = 24 * INCHES;

// Minimum front clearance per fixture type — the floor for S5
// clearance-shrink collision resolution. Set equal to the default
// `frontClearance` to disable shrink for that type (e.g. toilet/shower
// have no slack). For sinks, 24" matches the historical small-sink front
// clearance, recovering that capability without a separate fixture type.
/** Value of `object.clearanceProfile` that selects `MIN_FRONT_CLEARANCE`. */
const COMPACT_CLEARANCE_PROFILE = 'compact';

const MIN_FRONT_CLEARANCE = {
  // IRC R307.1 code minimum: 21" clear in front of a water closet. The
  // default stays the 30" comfort standard; S5's clearance-front shrink
  // may degrade toward this floor when a layout can't otherwise fit.
  toilet: 21 * INCHES,
  sink: 24 * INCHES,
  // Keyed by the CONFIG key the type matched, so the canonical kitchen sink
  // needs its own entry or a compact-profile one silently keeps the full 30".
  'kitchen-sink': 24 * INCHES,
  'double-sink': 30 * INCHES,
  shower: 24 * INCHES,
  bathtub: 30 * INCHES,
  // Appliance doors: 24" is the working minimum to stand in front of and
  // open a fridge (Talkeetna's fridge nook has 25" to the wall across it).
  refrigerator: 24 * INCHES,
  fridge: 24 * INCHES,
  'tub-shower': 24 * INCHES,
};

// Clearance values aligned with bathroom-analyzer pipeline (FIXTURE_DIMENSIONS)
// Front clearance = clearance.depth - physical.depth (consistently 30" in pipeline)
// Side clearance = (clearance.width - physical.width) / 2
const CLEARANCE_CONFIG = {
  toilet: {
    frontClearance: 30 * INCHES, // 30" clear in front (matches pipeline)
    sideClearance: 5 * INCHES, // 5" each side (pipeline: 30"w clearance for 20"w physical)
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  'lower-cabinet': {
    // Cabinet itself claims no front clearance — walking space is owned by
    // each child sink as a per-sink footprint. See
    // getCabinetClearanceFootprints in geometry.js.
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  sink: {
    frontClearance: 30 * INCHES,
    // Fixed-width basin clearance — does not grow with the vanity/cabinet
    // body during pipeline expansion.
    clearanceWidth: BASIN_CLEARANCE_WIDTH,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  // The canonical kitchen sink (`shared-lib/sink-types`), which supersedes the
  // legacy `{ type: 'sink', sinkType: 'kitchen' }` shape. It resolves to the
  // `sink` config above VERBATIM, because that is what the legacy shape
  // resolved to — this entry restores the 30" zone the rename dropped, it does
  // not introduce a new opinion. Without it a kitchen sink fell to
  // DEFAULT_CONFIG at 0", and because `getCabinetClearanceFootprints` reads
  // each sink child's OWN config, the sink-base cabinet emitted no footprint
  // either: the standing space in front of every kitchen sink was missing from
  // the drawing and from collision, silently, with nothing to see in either.
  // No `clearanceWidth`: unlike a bathroom basin, whose fixed 24" exists so the
  // zone does not grow to its VANITY's width, a kitchen sink object IS the
  // basin — 27-33" in the corpus — so the fixed width only under-covered it by
  // 3-9" at each end. The zone is the sink's own width.
  'kitchen-sink': {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  'double-sink': {
    // Pipeline-only fixture: clearanceCorners is the union AABB of two
    // pre-merge sink rectangles (each BASIN_CLEARANCE_WIDTH wide). No
    // standalone clearanceWidth override needed — the two per-sink
    // rectangles are preserved on `clearanceFootprints` after merge.
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  shower: {
    frontClearance: 24 * INCHES, // 24" clear in front
    clearanceWidth: 24 * INCHES, // 24" door opening width
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  bathtub: {
    frontClearance: 30 * INCHES, // 30" clear in front (matches pipeline)
    clearanceWidth: 24 * INCHES, // 24" access opening width (matches pipeline/shower)
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  // Tub-shower combo: 60" alcove with tub at bottom + shower overhead.
  // Step-in entry over tub rim; matched to shower clearance (24"×24").
  'tub-shower': {
    frontClearance: 24 * INCHES,
    clearanceWidth: 24 * INCHES,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: true,
  },
  // Shower sub-components — skip collision (they're inside the shower)
  showerhead: { skipCollisionCheck: true },
  'shower-controls': { skipCollisionCheck: true },
  'shower-bench': { skipCollisionCheck: true },
  'shower-niche': { skipCollisionCheck: true },
  // its accessories (step 3a) sit in its corners and on its walls
  'shower-shelf': { skipCollisionCheck: true },
  'corner-caddy': { skipCollisionCheck: true },
  'shower-seat': { skipCollisionCheck: true },
  'grab-bar': { skipCollisionCheck: true },
  'shower-door': { skipCollisionCheck: true },

  // Tub sub-components
  'tub-faucet': { skipCollisionCheck: true },
  'bathroom-tub-faucet': { skipCollisionCheck: true },
  'tub-drain': { skipCollisionCheck: true },

  // Faucet types — always part of a vanity group
  'bathroom-sink-faucet': { skipCollisionCheck: true },
  // Countertop-mounted sinks (undermount/dropin/vessel) skip collision here
  // because they're vanity sub-components. skipCollisionCheck is applied
  // dynamically in getClearanceConfig based on obj.sinkType rather than via
  // a flat CLEARANCE_CONFIG entry, so pedestal and wall-mount sinks still
  // get full clearance.

  // Wall-mounted fixtures — don't participate in collision checks. Their
  // 2D footprint can spuriously overlap tall floor fixtures (e.g. a switch
  // on the wall behind a shower) even though they live in different volumes.
  mirror: {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: false,
    isWallMounted: true,
    checkDoorSwing: false,
    skipCollisionCheck: true,
  },
  'wall-light': {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: false,
    isWallMounted: true,
    checkDoorSwing: false,
    skipCollisionCheck: true,
  },
  'overhead-wall-light': {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: false,
    isWallMounted: true,
    checkDoorSwing: false,
    skipCollisionCheck: true,
  },
  switch: {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: false,
    isWallMounted: true,
    checkDoorSwing: false,
    skipCollisionCheck: true,
  },
  // Ceiling-mounted fixtures live at ceiling height. Their 2D footprint
  // could spuriously overlap floor-level fixtures' clearance zones (e.g. a
  // pendant centered above a shower). The Y-axis check in fixtureCollision
  // would catch this only when center.y is correct; data with a missing
  // or zero center.y leaks through. Treat ceiling lights like wall-mounted
  // fixtures and skip floor-plane collision unconditionally.
  'ceiling-light': {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: false,
    checkDoorSwing: false,
    skipCollisionCheck: true,
  },
  countertop: {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: false,
    isWallMounted: false,
    checkDoorSwing: false,
    skipCollisionCheck: true, // countertop is part of vanity group, not standalone
  },
  // Decorative panel that fills the corner gap on the sink wall when a
  // single-sided corner cabinet sits on the perpendicular wall. ~1"
  // deep, body-less for collision purposes.
  'blank-panel': {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: false,
    skipCollisionCheck: true,
  },
  // Two side panels flanking a washer/dryer (`laundry-run-cabinets`).
  // Its bounding box is a SLOT, not a body — the machine stands inside it
  // by construction — so a solid-box collision test would report the
  // surround overlapping the very appliance it wraps. Body-less for
  // collision purposes, exactly like `blank-panel`.
  'laundry-surround': {
    frontClearance: 0,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: false,
    skipCollisionCheck: true,
  },

  // Kitchen appliances — 30" front clearance to load/unload, cook, or
  // open doors. Wall oven mounted at counter height (isFloorLevel: false).
  dishwasher: {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: false,
  },
  // Both old (stove) and new (range) keys map to the same config —
  // saved structures from before the GLB-aligned type rename keep working.
  stove: {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: false,
  },
  range: {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: false,
  },
  refrigerator: {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: true,
    checkDoorSwing: false,
  },
  // You stand in front of a cooktop to cook exactly as you do at a range, so
  // it claims the same 30". `isFloorLevel: false` because the body is cut into
  // the counter and seated on a base cabinet (like `oven`) — the STANDING
  // space is still floor. The type was known here only through
  // EXPECTED_OVERLAP_TYPES (counter, base cabinet) and had no clearance at all.
  cooktop: {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: false,
    checkDoorSwing: false,
  },
  oven: {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: false,
    checkDoorSwing: false,
  },
  'wall-oven': {
    frontClearance: 30 * INCHES,
    sideClearance: 0,
    isFloorLevel: false,
    checkDoorSwing: false,
  },
};

// Default config for unknown fixture types
const DEFAULT_CONFIG = {
  frontClearance: 0,
  sideClearance: 0,
  isFloorLevel: true,
  checkDoorSwing: false,
};

/**
 * Types that share kitchen aisle clearance with each other. In a real
 * kitchen the 30" "walking space" in front of a cabinet is the same
 * 30" walking space in front of an adjacent cabinet or appliance —
 * one person stands there and reaches multiple cabinets at once. So
 * cabinet-vs-cabinet, cabinet-vs-appliance, etc. clearance overlaps
 * are not collisions; they're the *whole point* of having a kitchen.
 *
 * Body overlaps between any two of these are still collisions (a
 * cabinet body and a fridge body cannot occupy the same space).
 *
 * Clearance against doors / walking-paths / non-kitchen objects is
 * still enforced (e.g., a refrigerator door clearance still mustn't
 * be blocked by a chair).
 */
const KITCHEN_SHARED_CLEARANCE_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  'fridge-surround',
  'countertop',
  SINK_TYPE,
  KITCHEN_SINK_TYPE,
  'kitchen-faucet',
  'cooktop',
  'dishwasher',
  // Both old (stove / range-hood / oven) and new (range / vent-hood /
  // wall-oven) names accepted — saved structures from before the GLB-
  // aligned type rename flow through this filter too.
  'stove',
  'range',
  'refrigerator',
  'oven',
  'wall-oven',
  'range-hood',
  'vent-hood',
]);

function sharesKitchenClearance(typeA, typeB) {
  return (
    KITCHEN_SHARED_CLEARANCE_TYPES.has((typeA || '').toLowerCase()) &&
    KITCHEN_SHARED_CLEARANCE_TYPES.has((typeB || '').toLowerCase())
  );
}

/**
 * Fixture types whose clearance zone must stay unobstructed even
 * by other kitchen items. A fridge door swings out and you pull
 * groceries through; a cabinet body in that zone literally blocks
 * the door. Same for ovens / dishwashers (load + unload), stoves
 * (you stand there and cook), and sinks (you stand there washing
 * dishes — a perpendicular cabinet body in the 30" zone makes the
 * sink unusable).
 *
 * Cabinets are NOT in this set — their "clearance" is just kitchen
 * aisle, which can be shared with any other kitchen item (see
 * `sharesKitchenClearance`).
 *
 * The single-sided corner cabinet rule (cornerDetection) requires
 * the sink's near edge to clear the perpendicular cabinet's 24"
 * footprint, so the legitimate sink-near-corner case stays valid.
 */
const STRICT_CLEARANCE_KITCHEN_TYPES = new Set([
  'refrigerator',
  'oven',
  'wall-oven',
  'dishwasher',
  // A cooktop's standing space is a range's by another name — you stand at it
  // with pans on the burners, and a cabinet body in that space is the same
  // obstruction.
  'cooktop',
  // Both old (stove) and new (range) names accepted — saved structures
  // from before the GLB-aligned type rename flow through this filter too.
  'stove',
  'range',
  SINK_TYPE,
  // The canonical spelling of the same fixture. `SINK_TYPE` has been strict
  // here since the set was written — the comment above describes washing up at
  // a sink — so leaving the renamed type out meant the rule applied to legacy
  // data and not to current data.
  KITCHEN_SINK_TYPE,
]);

function needsStrictKitchenClearance(type) {
  return STRICT_CLEARANCE_KITCHEN_TYPES.has((type || '').toLowerCase());
}

/**
 * Fixture type pairs that are expected to overlap (e.g., sink sits on countertop).
 * Pairs are unordered — [A, B] also matches [B, A].
 */
const EXPECTED_OVERLAP_TYPES = [
  // Canonical sink pairs
  [SINK_TYPE, 'countertop'],
  // The L-shaped corner cabinet sometimes sits on the same wall as a
  // sink (the wallA leg is part of the run that includes the sink
  // cabinet). Its body therefore sits inside the sink-basin clearance
  // just like any other lower cabinet on the run. Single-sided corner
  // cabinets on the perpendicular wall stay clear because
  // `sinkClearsCornerBox` enforces a 24" gap.
  [SINK_TYPE, 'corner-cabinet'],
  // NOTE: sink-vs-its-own-sink-cabinet and sink-vs-faucet are NOT
  // exempted by type pair anymore — they share a `fixtureGroupId` set
  // in materializeCandidate, so the same-group skip in
  // checkFixtureCollisions handles them. A blanket
  // [SINK_TYPE, 'lower-cabinet'] would also waive sink-vs-perpendicular
  // run-cabinet, which is the case the user wants flagged when a
  // regular cabinet on the adjoining wall blocks the sink user's
  // standing space.
  ['bathroom-sink-faucet', SINK_TYPE],
  ['bathroom-sink-faucet', 'countertop'],
  ['bathroom-sink-faucet', 'lower-cabinet'],
  ['countertop', 'lower-cabinet'],
  ['mirror', 'wall-light'],
  ['mirror', 'overhead-wall-light'],
  // Kitchen surround pairs — the cabinet that sits above a fridge or
  // on top of a stove physically touches the appliance's top in the
  // same wall slot. They are part of the same installation, not a real
  // collision.
  ['refrigerator', 'fridge-surround'],
  ['refrigerator', 'upper-cabinet'],
  ['fridge-surround', 'upper-cabinet'],
  ['fridge-surround', 'lower-cabinet'],
  ['fridge-surround', 'countertop'],
  // A wall oven is built into a column: it abuts the base section under it,
  // the cabinet over it, and the surround panels beside it. Same for the
  // surround itself against those sections.
  ['oven', 'oven-surround'],
  ['oven', 'lower-cabinet'],
  ['oven', 'upper-cabinet'],
  ['wall-oven', 'oven-surround'],
  ['wall-oven', 'lower-cabinet'],
  ['wall-oven', 'upper-cabinet'],
  ['oven-surround', 'lower-cabinet'],
  ['oven-surround', 'upper-cabinet'],
  ['oven-surround', 'countertop'],
  // A cooktop is cut into its countertop and seated on the cabinet below.
  ['cooktop', 'countertop'],
  ['cooktop', 'lower-cabinet'],
  // Both old and new type-name pairs accepted — saved structures keep working.
  ['stove', 'range-hood'],
  ['range', 'vent-hood'],
  ['stove', 'vent-hood'],
  ['range', 'range-hood'],
  ['chair', 'table'], // tucked-in dining chairs are a legitimate arrangement (observed 16" overlaps on A1A92296)
  // NOTE: `['countertop', <laundry type>]` pairs were added here for the
  // `laundry-run-cabinets` rule and have been removed as unreachable —
  // `countertop` carries `skipCollisionCheck: true`, so
  // `fixtureCollision.js` short-circuits in both directions before
  // `isExpectedOverlap` is consulted, and `isExpectedOverlap` keys on
  // `object.type` while most scanned machines carry `type: null` with only
  // `category.washerDryer`. The rule keeps its countertop clear of the
  // machines by deriving the run's height from them instead.
];

/**
 * Get clearance config for a fixture type.
 * Checks object.type first, then falls back to category keys.
 */
function getClearanceConfig(obj) {
  const type = (obj.type || '').toLowerCase();

  let config = DEFAULT_CONFIG;
  let matchedKey = null;
  if (CLEARANCE_CONFIG[type]) {
    config = CLEARANCE_CONFIG[type];
    matchedKey = type;
  } else if (obj.category) {
    for (const key of Object.keys(obj.category)) {
      const lk = key.toLowerCase();
      if (CLEARANCE_CONFIG[lk]) {
        config = CLEARANCE_CONFIG[lk];
        matchedKey = lk;
        break;
      }
    }
  }

  // Wall-hung toilets keep the 30" front + 5" side clearances of a
  // floor-mount toilet (bowl is still at sitting height), but the bowl
  // is suspended ~9" off the floor for cleanability, so it doesn't
  // reserve floor space under itself. Flag it as wall-mounted / non-
  // floor-level for the floor-occupancy checks, but keep collision on
  // — the bowl still overlaps other fixtures in plan view.
  if (matchedKey === 'toilet' && obj.mountType === 'wall-hung') {
    config = {
      ...config,
      isFloorLevel: false,
      isWallMounted: true,
    };
  }

  // Bathroom countertop-mounted sinks (undermount, dropin, vessel) are
  // vanity sub-components — collision lives on the parent lower-cabinet
  // / vanity. Pedestal and wall-mount sinks are standalone and need
  // full clearance. Kitchen sinks are also countertop-mounted but the
  // SINK is the canonical anchor for the 30" user zone — the parent
  // cabinet has type='lower-cabinet' and gets shared-aisle treatment,
  // which would silently allow a perpendicular cabinet body to sit in
  // the sink user's standing space. So we let kitchen sinks
  // participate in collision themselves; combined with `sink` being in
  // STRICT_CLEARANCE_KITCHEN_TYPES, that enforces the 30" zone.
  if (
    matchedKey === 'sink' &&
    COUNTERTOP_MOUNTED_SINK_TYPES.includes(obj.sinkType) &&
    obj.sinkType !== 'kitchen'
  ) {
    config = { ...config, skipCollisionCheck: true };
  }

  // Compact profile — stamped at scan time by clearance-resolution.js on a
  // fixture whose comfort front clearance physically cannot fit the room
  // (old houses: a 58" deep bath with a 30" toilet has no 30" in front of
  // it). Falls back to the code-minimum front clearance for the type
  // (`MIN_FRONT_CLEARANCE`, e.g. toilet 21" per IRC R307.1). Side
  // clearances are already code minimums and stay. Explicit per-object
  // custom clearances below still win.
  if (
    obj.clearanceProfile === COMPACT_CLEARANCE_PROFILE &&
    matchedKey &&
    MIN_FRONT_CLEARANCE[matchedKey] !== undefined &&
    MIN_FRONT_CLEARANCE[matchedKey] < config.frontClearance
  ) {
    config = {
      ...config,
      frontClearance: MIN_FRONT_CLEARANCE[matchedKey],
      clearanceProfile: COMPACT_CLEARANCE_PROFILE,
    };
  }

  // Apply per-object custom clearance overrides
  if (
    obj.customFrontClearance !== undefined ||
    obj.customClearanceWidth !== undefined
  ) {
    config = { ...config };
    if (obj.customFrontClearance !== undefined) {
      config.frontClearance = obj.customFrontClearance;
    }
    if (obj.customClearanceWidth !== undefined) {
      config.clearanceWidth = obj.customClearanceWidth;
    }
  }

  // Per-object skipCollisionCheck override — used by kitchen-layout
  // patches at non-standard (virtual) corners where the user has
  // accepted that two cabinet runs share space at the notch.
  if (obj.skipCollisionCheck === true && !config.skipCollisionCheck) {
    config = { ...config, skipCollisionCheck: true };
  }

  // Staged decor is dressing, not plumbing: it reserves no clearance of its
  // own and must never register against a fixture's. Without this a towel
  // basket or a plant standing inside a toilet's front-clearance polygon
  // raises `clearance-blocked` with the TOILET as the source — which is
  // actionable, so it lights the warning pill and offers Fix All, and Fix All
  // re-runs apply_bathroom_layout_v2, whose keep-list is 'stairs' and nothing
  // else. The prop is deleted, the warning clears, re-staging recreates it.
  if (isStagingDecor(obj) && !config.skipCollisionCheck) {
    config = { ...config, skipCollisionCheck: true };
  }

  return config;
}

/**
 * Check if two fixture types are expected to overlap (part of the same assembly).
 */
function isExpectedOverlap(typeA, typeB) {
  const a = (typeA || '').toLowerCase();
  const b = (typeB || '').toLowerCase();
  return EXPECTED_OVERLAP_TYPES.some(
    ([x, y]) => (a === x && b === y) || (a === y && b === x)
  );
}

/**
 * Compute total clearance zone dimensions from physical dimensions + clearance rules.
 * This is the single source of truth for how clearance zones are sized.
 *
 * @param {string} fixtureType - e.g. 'toilet', 'shower', 'bathtub'
 * @param {{width: number, depth: number}} physicalDims - Physical dimensions in meters
 * @param {Object} [metadata] - Optional object metadata (e.g. doorWidth)
 * @returns {{width: number, depth: number}} Total clearance zone dimensions in meters
 */
function computeClearanceDimensions(fixtureType, physicalDims, metadata = {}) {
  const config = CLEARANCE_CONFIG[fixtureType] || DEFAULT_CONFIG;
  if (config.skipCollisionCheck) return null;

  // Width: use explicit clearanceWidth (access opening) if defined,
  // otherwise derive from physical width + side clearance
  let width;
  if (metadata.doorWidth || metadata.openingWidth) {
    // Use actual door/opening width from object metadata
    width = Math.max(metadata.doorWidth || metadata.openingWidth, 24 * INCHES);
  } else if (config.clearanceWidth) {
    // Fixed access opening width (showers, undermount tubs)
    width = config.clearanceWidth;
  } else {
    // Physical width + side clearance (toilet, sink, freestanding tub)
    width = physicalDims.width + (config.sideClearance || 0) * 2;
  }

  // Depth: always physical depth + front clearance. The compact profile
  // (retry tier for rooms that yield zero layouts at comfort clearances)
  // uses the code-minimum front clearance instead — e.g. toilet 21"
  // (IRC R307.1) rather than the 30" comfort default.
  const front = metadata.compact
    ? (MIN_FRONT_CLEARANCE[fixtureType] ?? config.frontClearance)
    : config.frontClearance;
  const depth = physicalDims.depth + (front || 0);

  return { width, depth };
}

module.exports = {
  CLEARANCE_CONFIG,
  DEFAULT_CONFIG,
  EXPECTED_OVERLAP_TYPES,
  BASIN_CLEARANCE_WIDTH,
  MIN_FRONT_CLEARANCE,
  COMPACT_CLEARANCE_PROFILE,
  KITCHEN_SHARED_CLEARANCE_TYPES,
  STRICT_CLEARANCE_KITCHEN_TYPES,
  sharesKitchenClearance,
  needsStrictKitchenClearance,
  getClearanceConfig,
  isExpectedOverlap,
  computeClearanceDimensions,
};
